/* eslint-disable max-len */
// import { PainControlQuestionScreenName, PainControlOtherScreenName, PainControlScreenName } from 'hydra-data-types';
import {
  PainControlOtherScreenName,
  PainControlQuestionScreenName,
  PainControlScreenName,
} from 'hydra-data-types';
import { Gender } from '../../businessLogic/AssessmentQuestionTypes';
import * as images from '../../components/assets/Images';
import * as icons from '../../components/assets/images/Icons';
import { AuthLayoutConfig } from '../../components/flows/layouts/AuthLayout';
import {
  getAuthMethodUsed,
  getHasDoctor,
  getPaymentRoute,
} from '../../util/ConditionalNextScreenSelectors';
import { FlowConfig, ScreenConfig, ScreenType, SharedScreenName } from '../FlowTypes';
export type PainControlAssessmentScreen = SharedScreenName | PainControlScreenName;

const authLayout: AuthLayoutConfig = {
  registerTitle: 'One last step,\n so we can finalize your results!',
  registerBody: [
    'Enter your details to find out if addressing the overprotective state of your pain system can help you manage your pain.',
  ],
  registerPrivacyDeclaration: [
    'Relio will not sell any of your personal information. Ever. That’s a promise.',
  ],
  loginTitle: 'Welcome back!',
  registerButtonTitle: 'See my results',
  loginButtonTitle: 'See my program',
};

const NEXT_BUTTON_TITLE = 'Next';
const PAIN_CONTROL_SCREENS_PRE_RESULTS: Array<ScreenConfig<PainControlAssessmentScreen>> = [
  {
    screenName: SharedScreenName.Gender,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Great, we will start by understanding a bit more about you and your history with back pain.',
        'What’s your gender?',
      ],
      choices: [Gender.Male, Gender.Female, Gender.Nonbinary, Gender.Other, Gender.Unstated],
    },
    nextScreen: SharedScreenName.Age,
  },
  {
    screenName: SharedScreenName.Age,
    type: ScreenType.SingleInput,
    layout: {
      type: 'number',
      collectsPIIData: false,
      content: 'What’s your age?',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      inputLabel: 'Age',
      inputPlaceholder: 'Age',
      characterLimit: 3,
      minimumValue: 13,
    },
    nextScreen: SharedScreenName.Doctor,
  },
  {
    screenName: SharedScreenName.Doctor,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'A growing number of healthcare professionals are recommending Relio.',
        'Were you recommended by a healthcare professional (a doctor, a physiotherapist, etc.)?',
      ],
      choices: ['Yes', 'No'],
    },
    nextScreen: {
      No: PainControlOtherScreenName.SpeakToHCP,
      fallback: SharedScreenName.Referral,
    },
  },
  {
    screenName: PainControlOtherScreenName.SpeakToHCP,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'That’s okay. While you don’t need a referral to use Relio, it’s important not to self-diagnose.' +
            ' Other conditions can have similar symptoms.',
        },
        { type: 'image', src: images.doctor, altText: 'referral comparison' },
        {
          type: 'text',
          content:
            'Let’s continue with your assessment and then create your personalised back pain program.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.Attribution,
  },
  {
    screenName: SharedScreenName.Referral,
    type: ScreenType.Presentation,
    layout: {
      content: [
        { type: 'text', content: 'Thanks for letting us know!' },
        {
          type: 'text',
          content:
            'Over 5,000 healthcare professionals actively refer our programs to their patients' +
            ', and these patients see 2x better results.',
        },
        {
          type: 'image',
          src: images.referralComparisonPain,
          fullWidth: true,
          altText: 'referral comparison',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.Diagnosis,
  },
  {
    screenName: SharedScreenName.Attribution,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How did you find us?'],
      choices: [
        'Facebook',
        'TikTok',
        'App Store',
        'Influencer',
        'Friends or family',
        'Instagram',
        'Search engine (Google, Bing)',
        'Blog post or news article',
        'Magazine or print',
        'Support group',
        'Other',
      ],
      randomizeChoices: true,
    },
    nextScreen: PainControlQuestionScreenName.Diagnosis,
  },
  {
    screenName: PainControlQuestionScreenName.Diagnosis,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['What best describes the cause of your back pain?'],
      choices: [
        'Osteoarthritis',
        'Bulging disc(s)',
        'Herniated disc',
        'Disc degeneration',
        'Sciatica',
        'Sprain or strain',
        'Unsure',
        'Other',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.HowLong,
  },
  {
    screenName: SharedScreenName.HowLong,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How long have you had back pain and wanted to feel better?'],
      choices: ['0-6 months', '6-12 months', '1-5 years', '5+ years'],
    },
    nextScreen: PainControlQuestionScreenName.HowOften,
  },

  {
    screenName: PainControlQuestionScreenName.HowOften,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How often do you experience back pain?'],
      choices: ['Consistently every day', 'Multiple times a week', 'Weekly', 'Monthly'],
    },
    nextScreen: SharedScreenName.HealthIssues,
  },
  {
    screenName: SharedScreenName.HealthIssues,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        'It’s common for people with back pain to have multiple related conditions.',
        'Do you have any other health issues?',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Anxiety',
        'Stress',
        'Depression',
        'Migraine  headaches',
        'Fibromyalgia',
        'Endometriosis',
        'Pelvic pain',
        'IBS',
        'Sleep problems',
        'None',
        'Other',
      ],
    },
    nextScreen: PainControlQuestionScreenName.RelatePhysicalDamage,
  },
  relateQuestion(
    PainControlQuestionScreenName.RelatePhysicalDamage,
    'My pain is caused by physical damage in my back',
    PainControlOtherScreenName.PainPathwaysEdu1
  ),
  {
    screenName: PainControlOtherScreenName.PainPathwaysEdu1,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Persistent back pain is often misunderstood as solely physical, but research shows only 10% of cases are still caused by physical damage\u00B9.',
        },
        {
          type: 'text',
          content:
            'Instead, the majority of ongoing back pain is due to ingrained neural pathways that continue to generate pain even after the initial injury has healed.',
        },
        {
          type: 'image',
          src: images.painPathwaysEdu,
        },
        {
          type: 'text',
          content: 'Let’s see if your back pain falls into this category.',
        },
      ],
      footnotes: [
        'Maher, C., Underwood, M. and Buchbinder, R. (2017). Non-specific low back pain. The Lancet, [online] 389(10070), pp.736–747. doi:https://doi.org/10.1016/s0140-6736(16)30970-9.',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.RelatePainChanges,
  },
  relateQuestion(
    PainControlQuestionScreenName.RelatePainChanges,
    'My pain varies and changes. For example, it’s more severe on some days or worse in the morning/evening',
    PainControlQuestionScreenName.RelatePainAnticipation
  ),
  relateQuestion(
    PainControlQuestionScreenName.RelatePainAnticipation,
    'I anticipate pain before doing certain activities',
    PainControlQuestionScreenName.RelatePainBody
  ),
  relateQuestion(
    PainControlQuestionScreenName.RelatePainBody,
    'My pain spreads to different parts of my body',
    PainControlOtherScreenName.OverprotectivePainSystemEdu
  ),
  {
    screenName: PainControlOtherScreenName.OverprotectivePainSystemEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Even though these common experiences are non-physical in nature, they can still trigger back pain.',
        },
        {
          type: 'image',
          src: images.overprotectivePainSystemEdu,
          fullWidth: true,
        },
        {
          type: 'text',
          content: 'These are signs that your pain may be related to neural pathways in the brain.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.RelateThinkAboutItAllTheTime,
  },
  relateQuestion(
    PainControlQuestionScreenName.RelateThinkAboutItAllTheTime,
    'I think about my back most, if not all, of the time',
    PainControlQuestionScreenName.RelatePainWorseWhenStressed
  ),
  relateQuestion(
    PainControlQuestionScreenName.RelatePainWorseWhenStressed,
    'My pain is worse during times of high stress or emotion',
    PainControlQuestionScreenName.RelateRelaxDifficult
  ),
  relateQuestion(
    PainControlQuestionScreenName.RelateRelaxDifficult,
    'I find it difficult to relax or unwind',
    PainControlOtherScreenName.SystemActivationPainEdu
  ),
  {
    screenName: PainControlOtherScreenName.SystemActivationPainEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Continually focusing on your pain can reinforce neural pathways in the brain that generate pain.',
        },
        {
          type: 'text',
          content: 'Experiencing times of high stress and emotion can also amplify these pathways.',
        },
        {
          type: 'image',
          src: images.systemActivationPainEdu,
          fullWidth: true,
        },
      ],
    },
    nextScreen: PainControlQuestionScreenName.ImpactLife,
  },
  {
    screenName: PainControlQuestionScreenName.ImpactLife,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['How does dealing with these symptoms impact your life?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Physically uncomfortable',
        'Can’t do the activities I enjoy',
        'Worry about travel',
        'Struggle with work',
        'Not sleeping well',
        'More/Other',
      ],
    },
    nextScreen: PainControlQuestionScreenName.HaveTriedThese,
  },
  {
    screenName: PainControlQuestionScreenName.HaveTriedThese,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        "Finding the right solution can be difficult; some people end up feeling like they've tried everything. What options have you tried?",
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Physiotherapy',
        'Chiropractor',
        'Massage',
        'Exercise',
        'Pain medications',
        'Acupuncture',
        'Pain specialist',
        'Pain program',
        'Surgery',
        'Ergonomics',
        'Other',
      ],
    },
    nextScreen: PainControlQuestionScreenName.WasLastingReliefFound,
  },
  {
    screenName: PainControlQuestionScreenName.WasLastingReliefFound,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['Did these provide lasting relief from your pain?'],
      choices: ['Yes', 'No', 'The relief was temporary'],
    },
    nextScreen: {
      Yes: PainControlQuestionScreenName.TriedBackBrainBefore,
      fallback: PainControlQuestionScreenName.OtherDownsides,
    },
  },
  {
    screenName: PainControlQuestionScreenName.OtherDownsides,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Were there any other downsides of these treatments?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Too expensive',
        'Bad side effects',
        'High risk of complications',
        'Long waitlists',
        'Difficult to access',
        'Difficult to participate',
        'Other',
      ],
    },
    nextScreen: PainControlOtherScreenName.BackBrainTherepyWorksEdu,
  },
  {
    screenName: PainControlOtherScreenName.BackBrainTherepyWorksEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Gold standard clinical guidelines for persistent back pain\u00B9 state that for lasting relief, 3 key areas need to be addressed:',
        },
        {
          type: 'text',
          content: '- the back,',
        },
        {
          type: 'text',
          content: '- the brain, and',
        },
        {
          type: 'text',
          content: '- environmental influences.',
        },
        {
          type: 'image',
          src: images.backBrainTherapyWorksEdu,
          altText: 'back brain therapy',
        },
        {
          type: 'text',
          content: 'Despite this, most existing options only target the back.',
        },
      ],
      footnotes: [
        'Safetyandquality.gov.au. (2022). Low Back Pain Clinical Care Standard (2022) | Australian Commission on Safety and Quality in Health Care. [online] Available at: https://www.safetyandquality.gov.au/publications-and-resources/resource-library/low-back-pain-clinical-care-standard-2022.',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.TriedBackBrainBefore,
  },
  {
    screenName: PainControlQuestionScreenName.TriedBackBrainBefore,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['Have you ever tried an intervention that targets the brain before?'],
      choices: ['Yes', 'No'],
    },
    nextScreen: PainControlOtherScreenName.NeuRAResearchEdu,
  },
  {
    screenName: PainControlOtherScreenName.NeuRAResearchEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Research conducted by Neuroscience Research Australia (NeuRA) has shown that targeting neural pathways in the brain provided effective long-term pain management without the use of drugs, surgery, or physical therapy.\u00B9',
        },
      ],
      footnotes: [
        'Rizzo, R.R.N., Medeiros, F.C., Pires, L.G., Pimenta, R.M., McAuley, J.H., Jensen, M.P. and Costa, L.O.P. (2018). Hypnosis Enhances the Effects of Pain Education in Patients With Chronic Nonspecific Low Back Pain: A Randomized Controlled Trial. The Journal of Pain, 19(10), pp.1103.e1–1103.e9. doi:https://doi.org/10.1016/j.jpain.2018.03.013.',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.ImagineSixWeeks,
  },
  {
    screenName: PainControlQuestionScreenName.ImagineSixWeeks,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Imagine yourself in 6 weeks time, how would you like to feel?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Physically comfortable',
        'In control of my life',
        'Calm & worry free',
        'Healthier in my body',
        'Optimistic about the future',
        'Confident in myself',
        'More/Other',
      ],
    },
    nextScreen: PainControlQuestionScreenName.HowConfidentCurrentApproach,
  },
  {
    screenName: PainControlQuestionScreenName.HowConfidentCurrentApproach,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How confident are you in achieving this with your current approach?'],

      choices: ['Confident', 'Somewhat confident', 'Not at all confident'],
    },
    nextScreen: PainControlQuestionScreenName.WhatWouldChange,
  },
  {
    screenName: PainControlQuestionScreenName.WhatWouldChange,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['What would you want to do if you were free from the control of back pain?'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Activities I love',
        'Enjoy social life/relationships',
        'Sleep better',
        'Travel with confidence',
        'Be more present at work',
        'More/Other',
      ],
    },
    nextScreen: PainControlQuestionScreenName.HowImportantIsSuccess,
  },
  {
    screenName: PainControlQuestionScreenName.HowImportantIsSuccess,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How important is achieving this to you?'],
      choices: ['Very important', 'Important', 'Not important'],
    },
    nextScreen: SharedScreenName.Auth,
  },
  {
    screenName: SharedScreenName.Auth,
    type: ScreenType.Auth,
    layout: authLayout,
    nextScreen: {
      loginAndAlreadyPaid: SharedScreenName.AlreadyPaidDownloadApp,
      login: SharedScreenName.Results,
      register: SharedScreenName.LoadingResults,
      fallback: SharedScreenName.Results,
    },
    nextScreenSelector: getAuthMethodUsed,
  },
  {
    screenName: SharedScreenName.AlreadyPaidDownloadApp,
    type: ScreenType.DownloadApp,
    layout: {
      title: ['You already have access!'],
      body: ['Use the same email and password you signed up with:'],
      alreadyPaid: true,
    },
  },
  {
    screenName: SharedScreenName.LoadingResults,
    type: ScreenType.LoadingResults,
    layout: {
      reviewer: {
        name: 'Troy',
        testimonial: [
          '\u201CFrom personal experience and too much reliance on our medical system, I now know that with the right tools the mind and body can heal itself. 20 years of chronic lower back pain/sciatica and now pain free. It’s so empowering to heal yourself. This fantastic app taught me how to do that.\u201D',
        ],
        date: 'Australia',
      },
    },
    nextScreen: SharedScreenName.Results,
  },
  {
    screenName: SharedScreenName.Results,
    type: ScreenType.Results,
    layout: {
      title: 'The results are in',
      summaryBoxHeading: 'Relio is likely to work\nfor you!',
      summaryBoxBody:
        'Research shows 90% of people with persistent back pain would benefit from interventions that target the brain.',
      body: [
        'So far we’ve helped over 200,000 people with chronic conditions to achieve their goals.',
        'Let’s discover how Relio can help you.',
      ],
      primaryButtonTitle: 'Continue',
    },
    nextScreen: PainControlQuestionScreenName.ClinicalHypnosis,
  },
  {
    screenName: PainControlQuestionScreenName.ClinicalHypnosis,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Relio uses pain science education and clinical hypnosis to help you target the neural pathways in your brain that contribute to ongoing pain.',
        'When it comes to clinical hypnosis, you are:',
      ],
      choices: ['An expert', 'Curious', 'Uncertain'],
    },
    nextScreen: PainControlOtherScreenName.HypnosisLongHistoryEdu,
  },
  {
    screenName: PainControlOtherScreenName.HypnosisLongHistoryEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: 'Thanks for sharing!',
        },
        {
          type: 'text',
          content:
            'Clinical hypnosis has a long history but has only recently gained popularity for the effective management of persistent pain.',
        },
        {
          type: 'text',
          content:
            'A key turning point was a large 2021 review of 42 high-quality research studies that found people who engaged with clinical hypnosis reduced their pain by more than 73% compared to control participants\u00B9.',
        },
        { type: 'image', src: images.hypnosisLongHistory },
        {
          type: 'text',
          content:
            'Due to this large amount of evidence, leading back pain researchers at Neuroscience Research Australia conducted a breakthrough trial to test the impact of combining pain science education with clinical hypnosis.',
        },
        {
          type: 'text',
          content:
            'Their government-funded research showed that pairing clinical hypnosis with pain education doubled the number of people reporting reduced pain and improved function.',
        },
      ],
      footnotes: [
        'Milling, L.S., Valentine, K.E., LoStimolo, L.M., Nett, A.M. and McCarley, H.S. (2021). Hypnosis and the Alleviation of Clinical Pain: A Comprehensive Meta-Analysis. International Journal of Clinical and Experimental Hypnosis, 69(3), pp.297–322. doi:https://doi.org/10.1080/00207144.2021.1920330.',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.GuidedAudioExperience,
  },
  {
    screenName: PainControlQuestionScreenName.GuidedAudioExperience,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'Your personalized Relio program will be based on this successful research trial conducted by Neuroscience Research Australia.',
        'Do you have previous experience listening to guided audio?',
      ],
      choices: ['Yes', 'No'],
    },
    nextScreen: PainControlOtherScreenName.GuidedAudioEdu,
  },
  {
    screenName: PainControlOtherScreenName.GuidedAudioEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Familiarity with guided audio is useful but unlike audio for general relaxation, clinical hypnosis has been designed to directly target the neural pathways in your brain that are causing ongoing pain.',
        },
        {
          type: 'image',
          src: images.guidedAudioPathways,
          altText: 'Guided Audio',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlOtherScreenName.ProgressToCalmEdu,
  },
  {
    screenName: PainControlOtherScreenName.ProgressToCalmEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'Each session is like a lesson that will help you re-route the neural pathways contributing to your ongoing pain.',
        },
        {
          type: 'image',
          src: images.progressToCalmPathways,
          fullWidth: true,
          altText: 'Back brain communication improves over time',
        },
        {
          type: 'text',
          content:
            'Over time, you can form new pathways in your brain that no longer generate pain.',
        },
      ],
    },
    nextScreen: PainControlQuestionScreenName.ResultsDateReached,
  },
  {
    screenName: PainControlQuestionScreenName.ResultsDateReached,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        'As a part of this program, you’ll receive a Results Date - the point at which you can expect to have better managed your back pain and returned to meaningful activities.',
        'When I reach my Results Date, I’d like to have: (select all that apply)',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Less worry about my back pain',
        'Increased movement and activity',
        'Better sleep',
        'Increased energy levels',
        'More/other',
      ],
    },
    nextScreen: PainControlQuestionScreenName.ResultsDateAfter,
  },
  {
    screenName: PainControlQuestionScreenName.ResultsDateAfter,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['After my results date, I’d love to (select all that apply)'],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      choices: [
        'Move freely and without worry',
        'Travel easily',
        'Socialize without interruptions',
        'Enjoy new activities that I’ve been scared to pursue',
        'Be more present in my relationships',
        'More/other',
      ],
    },
    nextScreen: PainControlOtherScreenName.ResultsDateIntro,
  },
  {
    screenName: PainControlOtherScreenName.ResultsDateIntro,
    type: ScreenType.Presentation,
    layout: {
      title: 'Your Results Date',
      content: [
        {
          type: 'text',
          content: 'Great!',
        },
        {
          type: 'text',
          content:
            'The next set of questions will help us determine your Results Date and when you can expect to begin enjoying some of the benefits you’ve selected.',
        },
        {
          type: 'image',
          src: images.resultsDatePain,
          altText: 'Sustained progress from today until your results date',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.TimeCommitment,
  },
  {
    screenName: PainControlQuestionScreenName.TimeCommitment,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How much time can you commit each day?'],
      choices: ['15 minutes', '15-30 minutes', '30-60 minutes', 'More than 1 hour'],
    },
    nextScreen: PainControlQuestionScreenName.TimeOfDayPain,
  },

  {
    screenName: PainControlQuestionScreenName.TimeOfDayPain,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['What’s the best time in your day to complete your Relio session?'],
      choices: [
        'When I first wake up',
        'In the morning',
        'At lunchtime',
        'In the afternoon/evening',
        'Before I fall asleep',
      ],
    },
    nextScreen: PainControlQuestionScreenName.DaysPerWeekPain,
  },

  {
    screenName: PainControlQuestionScreenName.DaysPerWeekPain,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'People who use the program more than 5 times per week see the best results.',
        'How many days can you listen to Relio each week?',
      ],
      choices: ['3 days', '4 days', '5 days', '6 days', '7 days'],
    },
    nextScreen: PainControlOtherScreenName.BuildProgramPain,
  },

  {
    screenName: PainControlOtherScreenName.BuildProgramPain,
    type: ScreenType.SocialProof,
    layout: {
      body: [
        'Great! Completing sessions regularly is the best way to see results.',
        'We’ll use this information to predict your Results Date and build a program that works for you.',
      ],
      reviewer: {
        name: 'Ben',
        testimonial: [
          '\u201CRelio has definitely had a huge impact on my recovery. I am now back to running, cycling, bodyweight workouts and getting stronger every week in the gym. Critically my pain is just continuing to dial down and fade away. I am no longer stressed about long periods of sitting (eg driving or flying), and I can effectively deal with the stress responses from work that historically have made the pain very difficult to manage.\u201D',
        ],
        date: 'Australia',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlQuestionScreenName.AccountabilityAndMotivation,
  },

  {
    screenName: PainControlQuestionScreenName.AccountabilityAndMotivation,
    type: ScreenType.MultiSelect,
    layout: {
      /**
				 * What would help you stay consistent while using Relio?
 
				 */
      body: ['What would help you stay consistent while using Relio?'],
      choices: [
        'Regular check-ins',
        'Encouraging messages',
        'Recovery stories',
        'Friendly reminders',
        'Other',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlOtherScreenName.AddressMotivation,
  },
  {
    screenName: PainControlOtherScreenName.AddressMotivation,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content: 'We’ve added this to your program to help you stay consistent.',
        },
        {
          type: 'image',
          src: images.addressMotivation,
          fullWidth: true,
          altText: 'Address Motivation',
        },
        {
          type: 'text',
          content:
            'Regular check-ins, encouraging messages, recovery stories, and friendly reminders are all available to help you stay motivated during the program.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlOtherScreenName.PercentageImproveByResultDateEdu,
  },
  {
    screenName: PainControlOtherScreenName.PercentageImproveByResultDateEdu,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            '89% of members on our programs improve their symptom management by their Results Date.',
        },
        {
          type: 'image',
          src: images.percentageImproveByResultsDate,
          fullWidth: true,
          altText:
            'more than 15000 reviews, covered by New York Tims, Wall Street Journal and N.P.R.',
        },
        {
          type: 'text',
          content:
            'We’ll now create your personalized Relio Back Pain program and predict your Results Date.',
        },
      ],
    },
    nextScreen: PainControlOtherScreenName.LoadingProgram,
  },
  {
    screenName: PainControlOtherScreenName.LoadingProgram,
    type: ScreenType.LoadingResults,
    layout: {
      reviewer: {
        name: 'Joe',
        testimonial: [
          '\u201CStarted using this after seeing an ad on instagram. Unlike most of those ads, there is genuine research behind this. You can opt to pay just £1 for a week’s trial. After that it’s the price of a physio session for a year, and that is astounding value.\u201D',
        ],
        date: 'United Kingdom',
      },
    },
    nextScreen: PainControlOtherScreenName.ResultsDate,
  },
  {
    screenName: PainControlOtherScreenName.ResultsDate,
    type: ScreenType.ProgramExperience,
    layout: {
      title: 'You can expect to start seeing results in 4-6 weeks',
      topBody: [
        'Based on your current personalized program, we predict that you can expect to see results and achieve your goals by:',
      ],
      showDate: true,
      bottomBody: [
        'This prediction is based on the results of over 58,000 members who have completed our programs.',
      ],
      listTitle: 'In preparation for your Results Date, you will:',
      listItems: [
        { image: icons.volume, desc: 'Listen to daily clinical hypnosis' },
        { image: icons.books, desc: 'Learn about pain science' },
        // { image: icons.air, desc: 'Follow breathing exercises' },
        { image: icons.medal, desc: 'Set milestones' },
        { image: icons.graph, desc: 'Track your progress' },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.ProgramStructure,
  },
  {
    screenName: SharedScreenName.ProgramStructure,
    type: ScreenType.ProgramStructure,
    layout: {
      title: 'Your Program is Ready!',
      // where can we put "Manage your back pain symptoms within 6 weeks"?
      coreProgramWeekTitles: [
        'Trial the program to experience clinical hypnosis',
        'Understand the cause of your back pain',
        'Rewire your neural pathways',
        'Return to your daily activities',
        'Build skills to address stress and emotions',
        'Better manage your pain symptoms',
      ],
      postProgramTitle: 'Beyond Week 6',
      postProgramContent: 'Maintain improvements long-term',
      primaryButtonTitle: 'Get Started',
      hideDoc: true,
    },
    nextScreen: PainControlQuestionScreenName.BestSupported,
  },
  {
    screenName: PainControlQuestionScreenName.BestSupported,
    type: ScreenType.MultiSelect,
    layout: {
      body: [
        'Relio can help you better manage back pain symptoms in just 6 weeks, but it can also ensure you maintain these results long-term.',
        'How can we best support you after your Results Date?',
      ],
      choices: [
        'Help calm any flare-ups',
        'Help maintain my results',
        'Help improve other areas of my life (e.g. sleep, mental health)',
        'Help reduce my stress/anxiety',
        'Other',
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.MaintenanceExperience,
  },
  {
    screenName: SharedScreenName.MaintenanceExperience,
    type: ScreenType.Presentation,
    layout: {
      title: 'Maintaining your Results',
      content: [
        {
          type: 'text',
          content:
            "After your Results Date in 6 weeks, you'll maintain access to the program and unlock customizable support tools to effectively manage your back pain long-term.",
        },
        { type: 'image', src: images.maintenanceExperiencePain, fullWidth: true },
        {
          type: 'text',
          content:
            'These tools are built into the program to ensure you sustain the results you have achieved.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: PainControlOtherScreenName.MoneyBackGuarantee,
  },
  {
    screenName: PainControlOtherScreenName.MoneyBackGuarantee,
    type: ScreenType.Presentation,
    layout: {
      title: 'Our Money Back Guarantee',
      content: [
        {
          type: 'text',
          content:
            'If you aren’t happy with your results within the first 6 weeks of the program, we’ll give you a full refund.',
        },
        { type: 'image', src: images.moneyBackGuaranteePain, fullWidth: true },
        {
          type: 'text',
          content:
            'To claim your refund, simply email our support team at relio@mindsethealth.com or message them via our chat support within the program.',
        },
      ],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: SharedScreenName.TrialExplanation,
  },
  {
    screenName: SharedScreenName.TrialExplanation,
    type: ScreenType.SingleSelect,
    layout: {
      body: [
        'In your 7-day trial, you’ll have access to the first week of your personalized program.',
        'Will you commit to completing 5 sessions this week to give yourself the best chance of success?',
      ],
      choices: ['Yes', 'No', 'Maybe'],
    },
    nextScreen: {
      Yes: SharedScreenName.WorksBest,
      fallback: PainControlOtherScreenName.SlowerResults,
    },
  },
  {
    screenName: PainControlOtherScreenName.SlowerResults,
    type: ScreenType.Presentation,
    layout: {
      content: [
        {
          type: 'text',
          content:
            'People who complete at least 5 sessions per week are 6x more likely to complete the program.',
        },
        {
          type: 'text',
          content:
            'If you feel like you won’t be able to commit to this, that’s okay. Relio can still work for you, but it will take longer to see results.',
        },
      ],
    },
    nextScreen: {
      hasDoctor: SharedScreenName.Payment,
      fallback: SharedScreenName.WorksBest,
    },
    nextScreenSelector: getHasDoctor,
  },

  {
    screenName: SharedScreenName.WorksBest,
    type: ScreenType.WorksBest,
    layout: {
      title: 'What works best for you?',
      body: [
        'Your health is our priority.',
        'We believe money shouldn’t stop you from exploring something that could change your life - so we’re letting you choose the amount to pay.',
      ],
      description: [
        'It costs us {{trialPrice}} to provide a {{trialPeriod}}-day trial.',
        'Please choose the amount below that best supports your circumstance.',
      ],
      primaryButtonTitle: 'Continue',
    },
    nextScreen: SharedScreenName.Payment,
  },
  {
    screenName: SharedScreenName.Payment,
    type: ScreenType.Payment,
    showHeaderBar: false,
    nextScreen: {
      newUser: SharedScreenName.FinishAccount,
      existingUser: SharedScreenName.DownloadApp,
      fallback: SharedScreenName.DownloadApp,
    },
    nextScreenSelector: getPaymentRoute,
  },
  {
    screenName: SharedScreenName.FinishAccount,
    type: ScreenType.FinishAccount,
    layout: {
      // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
      // Title and info coming from translation JSON to avoid 'missing translation' bug.
      subtitle: 'Fill in these last few details and you’re ready to go!',
      primaryButtonTitle: 'Continue',
    },
    nextScreen: SharedScreenName.DownloadApp,
  },
  {
    screenName: SharedScreenName.DownloadApp,
    type: ScreenType.DownloadApp,
    layout: {
      title: ['You’re all set!', 'Access your program now'],
      // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
      // body coming from translation JSON to avoid 'missing translation' bug.
      alreadyPaid: false,
    },
  },
];

export const PAIN_CONTROL_CONFIG: FlowConfig<PainControlAssessmentScreen> = {
  name: 'PainControl',
  showProgressBar: true,
  // We fire an `o-flow-entered` event manually from the `Onboard` page (see `trackFlowEnteredAnalytics`) instead of
  // here because it needs to include traits derived from experiments being run.
  startEvent: undefined,
  screens: [...PAIN_CONTROL_SCREENS_PRE_RESULTS],
};

export function relateQuestion(
  screenName: PainControlAssessmentScreen,
  statement: string,
  nextScreen: PainControlAssessmentScreen
): ScreenConfig<PainControlAssessmentScreen> {
  return {
    screenName,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['Do you relate to the following statement?', `\u201C${statement}\u201D`],
      choices: ['Yes', 'Somewhat', 'No'],
    },
    nextScreen,
  };
}
